/**
 * 🚀 Request Cache Service
 * 
 * Verhindert redundante API-Calls durch intelligentes Caching
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  promise?: Promise<T>;
}

class RequestCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 30000; // 30 Sekunden Cache

  constructor() {
    // 🔄 Cache bei Tab-Wechsel zurück invalidieren
    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          // Tab wieder aktiv - Dashboard Cache invalidieren für frische Daten
        }
      });
    }
  }

  /**
   * Cached API Call - verhindert redundante Requests
   */
  async cachedCall<T>(
    key: string, 
    apiCall: () => Promise<T>, 
    ttl: number = this.DEFAULT_TTL
  ): Promise<T> {
    const now = Date.now();
    const entry = this.cache.get(key);

    // 1. Cache Hit - gültige Daten vorhanden
    if (entry && (now - entry.timestamp) < ttl) {
      return entry.data;
    }

    // 2. Request bereits in Bearbeitung - warte darauf
    if (entry?.promise) {
      return entry.promise;
    }

    // 3. Neuer Request - erstelle Promise und cachen
    
    const promise = apiCall().then(data => {
      // Request erfolgreich - Daten cachen
      this.cache.set(key, {
        data,
        timestamp: now,
        promise: undefined // Promise entfernen nach Completion
      });
      return data;
    }).catch(error => {
      // Fehler - Cache-Eintrag entfernen
      this.cache.delete(key);
      throw error;
    });

    // Promise temporär cachen um redundante Calls zu verhindern
    this.cache.set(key, {
      data: entry?.data || null,
      timestamp: entry?.timestamp || 0,
      promise
    });

    return promise;
  }

  /**
   * Cache invalidieren
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Alle Cache-Einträge löschen
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Cache-Status für Debugging
   */
  getStatus(): { key: string; age: number; hasData: boolean }[] {
    const now = Date.now();
    return Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      age: now - entry.timestamp,
      hasData: entry.data !== null
    }));
  }
}

// Singleton Instance
export const requestCache = new RequestCache();

// Cache Keys - zentral verwaltet
export const CACHE_KEYS = {
  USER_ROLE: 'user-role',
} as const; 